import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Calendar, Target } from "lucide-react";
import { format } from "date-fns";

interface LinkedInAdsCardProps {
  adsData: {
    hasActiveAds: boolean;
    adCount: number;
    campaigns?: Array<{
      title: string;
      description: string;
      imageUrl?: string;
      ctaText: string;
      detectedAt: string;
      landingPageUrl?: string;
      firstSeenDate?: string;
      lastSeenDate?: string;
      thumbnails?: string[];
    }>;
    dateRange?: {
      earliest: string;
      latest: string;
    };
    metadata?: {
      note?: string;
      checkedAt?: string;
    };
  };
  companyName: string;
  isCompetitor?: boolean;
}

export function LinkedInAdsCard({ adsData, companyName, isCompetitor = false }: LinkedInAdsCardProps) {
  if (!adsData.hasActiveAds || !adsData.campaigns || adsData.campaigns.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-muted rounded-lg">
            <Target className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold">Reklamy LinkedIn</h3>
            <p className="text-sm text-muted-foreground">
              {isCompetitor ? 'Konkurent' : 'Firma'}: {companyName}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8 text-center">
          <div className="text-muted-foreground">
            <Target className="w-12 h-12 mx-auto mb-2 opacity-20" />
            <p className="text-sm">Brak aktywnych reklam</p>
            {adsData.metadata?.checkedAt && (
              <p className="text-xs mt-1">
                Sprawdzono: {format(new Date(adsData.metadata.checkedAt), "d MMM yyyy, HH:mm")}
              </p>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Reklamy LinkedIn</h3>
            <p className="text-sm text-muted-foreground">
              {isCompetitor ? 'Konkurent' : 'Firma'}: {companyName}
            </p>
          </div>
        </div>
        <Badge variant="default" className="ml-2">
          {adsData.adCount} {adsData.adCount === 1 ? 'reklama' : 'reklam'}
        </Badge>
      </div>

      {adsData.dateRange && (
        <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>
            Aktywne: {format(new Date(adsData.dateRange.earliest), "d MMM yyyy")} - {format(new Date(adsData.dateRange.latest), "d MMM yyyy")}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {adsData.campaigns.map((campaign, index) => (
          <Card key={index} className="overflow-hidden hover:shadow-md transition-shadow">
            {(campaign.imageUrl || (campaign.thumbnails && campaign.thumbnails.length > 0)) && (
              <div className="aspect-video bg-muted relative overflow-hidden">
                <img
                  src={campaign.imageUrl || campaign.thumbnails?.[0]}
                  alt={campaign.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
            <div className="p-4">
              <h4 className="font-semibold mb-2 line-clamp-2">{campaign.title}</h4>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                {campaign.description}
              </p>
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="text-xs">
                  {campaign.ctaText}
                </Badge>
                {campaign.landingPageUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                  >
                    <a
                      href={campaign.landingPageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span className="text-xs">Zobacz</span>
                    </a>
                  </Button>
                )}
              </div>
              {(campaign.firstSeenDate || campaign.lastSeenDate) && (
                <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                  {campaign.firstSeenDate && (
                    <div>Pierwsze wykrycie: {format(new Date(campaign.firstSeenDate), "d MMM yyyy")}</div>
                  )}
                  {campaign.lastSeenDate && (
                    <div>Ostatnie wykrycie: {format(new Date(campaign.lastSeenDate), "d MMM yyyy")}</div>
                  )}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {adsData.metadata?.note && (
        <div className="mt-4 p-3 bg-muted rounded-lg text-sm text-muted-foreground">
          {adsData.metadata.note}
        </div>
      )}

      {adsData.metadata?.checkedAt && (
        <div className="mt-4 text-xs text-muted-foreground text-center">
          Sprawdzone w LinkedIn Ad Library: {format(new Date(adsData.metadata.checkedAt), "d MMMM yyyy, HH:mm")}
        </div>
      )}
    </Card>
  );
}
